import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { useTranslation } from '../i18n/LanguageContext';
import { useThemeColor } from '../hooks/useThemeColor';
import { fetchOrganizationColor } from '../slice/themeSlice';
import { BASE_AUTH_URL, BASE_URL } from '../components/constants';
// Capital L matches the file on disk; the container builds on Linux, where
// '../assets/logo.png' would not resolve.
import LoginLogo from '../assets/Logo.png';
import MfaEnrollment from '../components/MfaEnrollment';

// Logo.png is a 1536x262 wordmark, so it is sized by width with auto height —
// the old square h-20 w-20 box rendered it at 80x13px inside 80px of padding.
const LogoBadge = () => (
  <div className="flex items-center justify-center mb-6">
    <img src={LoginLogo} alt="Logo" className="w-48 h-auto object-contain" />
  </div>
);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MicrosoftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const BackArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5" />
    <path d="M12 19l-7-7 7-7" />
  </svg>
);

const ShieldIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9.5 12l1.8 1.8L14.5 10" />
  </svg>
);

const Spinner = () => (
  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const Login = () => {
  const { t } = useTranslation();
  const { primaryColor } = useThemeColor();
  const dispatch = useDispatch();

  // 'login' | 'mfa-verify' | 'mfa-setup'
  const [step, setStep] = useState('login');
  const [email, setEmail] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaSetupToken, setMfaSetupToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!EMAIL_REGEX.test(email.trim())) return;
    const timer = setTimeout(() => {
      dispatch(fetchOrganizationColor(email.trim()));
    }, 400);
    return () => clearTimeout(timer);
  }, [email, dispatch]);

  // SSO callback: pick up mfa_token, mfa_setup_token, or a deactivated-account error from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get('sso_error');
    if (ssoError) {
      window.history.replaceState({}, '', window.location.pathname);
      setError(ssoError);
      return;
    }
    const ssoEmail = params.get('email');
    const incomingMfaToken = params.get('mfa_token');
    const incomingMfaSetupToken = params.get('mfa_setup_token');
    if (!ssoEmail || (!incomingMfaToken && !incomingMfaSetupToken)) return;
    window.history.replaceState({}, '', window.location.pathname);
    setEmail(ssoEmail);
    if (incomingMfaToken) {
      setMfaToken(incomingMfaToken);
      goToStep('mfa-verify');
    } else {
      setMfaSetupToken(incomingMfaSetupToken);
      goToStep('mfa-setup');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToStep = (newStep) => { setError(''); setStep(newStep); };

  const goBack = () => {
    setEmail('');
    setMfaToken('');
    setMfaSetupToken('');
    setMfaCode('');
    setUseRecoveryCode(false);
    goToStep('login');
  };

  const completeLogin = (data) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('email', data.email);
    localStorage.setItem('name', data.name);
    localStorage.setItem('id', data.id);
    localStorage.setItem('language', 'de');
    window.location.replace('/');
  };

  const handleError = (err) => setError(err?.response?.data?.detail || t('auth.genericError'));

  const continueWithMicrosoft = () => { window.location.href = `${BASE_URL}/microsoft/login`; };

  const submitMfaVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(
        `${BASE_AUTH_URL}/mfa/verify`,
        { email: email.trim(), code: mfaCode },
        { headers: { Authorization: `Bearer ${mfaToken}` } }
      );
      completeLogin(res.data);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const primaryButtonClass = 'w-full py-3.5 rounded-xl text-white text-[15px] font-semibold transition hover:brightness-90 active:brightness-75 disabled:opacity-60 disabled:hover:brightness-100 flex items-center justify-center gap-2';
  const outlineButtonClass = 'w-full py-3.5 rounded-xl border border-gray-300 text-[15px] font-medium text-gray-800 flex items-center justify-center gap-2.5 hover:bg-gray-50 transition disabled:opacity-60';
  const linkButtonClass = 'text-[14px] font-medium transition hover:opacity-75';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F2F2F2] p-6">
      <div className="relative bg-white rounded-[24px] shadow-[0_4px_48px_rgba(0,0,0,0.10)] w-full max-w-[440px] px-10 py-10">

        {step !== 'login' && (
          <button type="button" onClick={goBack}
            className="absolute top-6 left-6 p-1 text-gray-500 hover:text-gray-800 transition" aria-label={t('common.back')}>
            <BackArrowIcon />
          </button>
        )}

        {error && (
          <div className="mb-5 text-[13px] text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">{error}</div>
        )}

        {/* ── MAIN LOGIN SCREEN — Microsoft SSO is the only sign-in route ── */}
        {step === 'login' && (
          <div>
            <LogoBadge />

            <h1 className="text-[28px] font-bold text-center mb-1.5 tracking-tight text-gray-900">
              {t('auth.welcomeBack')}
            </h1>
            <p className="text-gray-400 text-center text-[14px] mb-7 leading-relaxed">
              {t('auth.loginPageSubtitle')}
            </p>

            <button type="button" onClick={continueWithMicrosoft} className={outlineButtonClass}>
              <MicrosoftIcon />
              {t('auth.continueWithMicrosoft')}
            </button>
          </div>
        )}

        {/* ── MFA VERIFY ── */}
        {step === 'mfa-verify' && (
          <form onSubmit={submitMfaVerify}>
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}1A` }}>
                <ShieldIcon color={primaryColor} />
              </div>
            </div>
            <h1 className="text-[26px] font-semibold text-center mb-2 tracking-tight text-gray-900">{t('auth.mfaVerifyTitle')}</h1>
            <p className="text-gray-500 text-center text-[14px] mb-8 leading-relaxed">
              {useRecoveryCode ? t('auth.mfaRecoveryVerifySubtitle') : t('auth.mfaVerifySubtitle')}
            </p>
            <input type="text" inputMode={useRecoveryCode ? 'text' : 'numeric'} value={mfaCode} onChange={(e) => setMfaCode(e.target.value)}
              placeholder={useRecoveryCode ? 'xxxxx-xxxxx' : '••••••'} autoFocus autoComplete="one-time-code"
              className={`w-full border border-gray-300 rounded-2xl px-4 py-[14px] text-center font-semibold outline-none focus:border-2 focus:border-[var(--aispace-color)] transition-[border] ${useRecoveryCode ? 'text-[18px] tracking-[3px] font-mono' : 'text-[22px] tracking-[8px]'}`}
            />
            <button type="submit" disabled={loading} className={primaryButtonClass + ' mt-5'} style={{ backgroundColor: primaryColor }}>
              {loading ? <Spinner /> : t('common.continue')}
            </button>
            <div className="text-center mt-4">
              <button type="button" onClick={() => { setUseRecoveryCode((v) => !v); setMfaCode(''); setError(''); }}
                className={linkButtonClass} style={{ color: primaryColor }}>
                {useRecoveryCode ? t('auth.mfaUseAuthenticatorCode') : t('auth.mfaUseRecoveryCode')}
              </button>
            </div>
          </form>
        )}

        {/* ── MFA SETUP ── */}
        {step === 'mfa-setup' && (
          <div>
            <LogoBadge />
            <h1 className="text-[24px] font-semibold text-center mb-2 tracking-tight text-gray-900">{t('auth.mfaSetupTitle')}</h1>
            <MfaEnrollment authorization={`Bearer ${mfaSetupToken}`} primaryColor={primaryColor} onComplete={completeLogin} />
          </div>
        )}

      </div>
    </div>
  );
};

export default Login;
